import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, Plus, Loader2, Sparkles, Trash2, Crown } from "lucide-react";
import AppShell from "../components/AppShell";
import UpgradeDialog from "../components/UpgradeDialog";
import { api, streamChat, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const SUGGESTIONS = [
    "Where did my money go last month?",
    "Which subscriptions am I paying for?",
    "How much did I spend on takeaways vs eating out?",
    "I've got £600 until payday — plan my spending.",
    "Find me £150 a month of savings.",
    "What bills should I shop around for?",
];

function renderMd(text) {
    const inline = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, i) => p.startsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : p);
    const blocks = text.split(/\n{2,}/);
    return blocks.map((b, i) => {
        const lines = b.split("\n");
        if (lines.every((l) => /^\s*([-*•]|\d+[.)])\s+/.test(l))) {
            return <ul key={i}>{lines.map((l, j) => <li key={j}>{inline(l.replace(/^\s*([-*•]|\d+[.)])\s+/, ""))}</li>)}</ul>;
        }
        return <p key={i}>{lines.map((l, j) => <span key={j}>{inline(l)}{j < lines.length - 1 && <br />}</span>)}</p>;
    });
}

export default function Chat() {
    const { isPro } = useAuth();
    const [params, setParams] = useSearchParams();
    const [sessions, setSessions] = useState([]);
    const [sid, setSid] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState(params.get("q") || "");
    const [busy, setBusy] = useState(false);
    const [quota, setQuota] = useState(null);
    const [upgrade, setUpgrade] = useState(false);
    const endRef = useRef();

    const loadSessions = () => api.get("/chat/sessions").then((r) => { setSessions(r.data); return r.data; });
    const loadQuota = () => api.get("/chat/quota").then((r) => setQuota(r.data));

    useEffect(() => {
        loadQuota();
        loadSessions().then((s) => { if (s.length) open(s[0].id); });
        if (params.get("q")) { const p = new URLSearchParams(params); p.delete("q"); setParams(p, { replace: true }); }
        // eslint-disable-next-line
    }, []);
    useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

    const open = async (id) => { setSid(id); const { data } = await api.get(`/chat/sessions/${id}/messages`); setMessages(data); };
    const newChat = async () => { const { data } = await api.post("/chat/sessions"); setSid(data.id); setMessages([]); loadSessions(); return data.id; };
    const remove = async (id) => { await api.delete(`/chat/sessions/${id}`); const s = await loadSessions(); if (sid === id) { s.length ? open(s[0].id) : (setSid(null), setMessages([])); } };

    const send = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || busy) return;
        if (quota && !quota.unlimited && quota.remaining <= 0) return setUpgrade(true);
        let id = sid || (await newChat());
        setInput("");
        setMessages((m) => [...m, { id: "u" + Date.now(), role: "user", content: msg }, { id: "a" + Date.now(), role: "assistant", content: "", streaming: true }]);
        setBusy(true);
        try {
            await streamChat(id, msg, (delta) => setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + delta }; return c; }));
            setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], streaming: false }; return c; });
            loadSessions(); loadQuota();
        } catch (e) {
            setMessages((m) => m.slice(0, -2));
            if (e.status === 403) setUpgrade(true); else toast.error(e.message || "Chat failed");
        } finally { setBusy(false); }
    };

    return (
        <AppShell title="Ask Sift" subtitle="Talk to your money" testid="chat-page"
            actions={quota && !quota.unlimited ? (
                <button onClick={() => setUpgrade(true)} data-testid="chat-quota" className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground">
                    <Crown className="h-3 w-3 text-accent" /> {quota.remaining} of {quota.limit} free questions left today
                </button>
            ) : null}>
            <UpgradeDialog open={upgrade} onOpenChange={setUpgrade} feature="Unlimited Ask Sift" />
            <div className="grid gap-4 lg:grid-cols-4">
                <aside className="hidden lg:block" data-testid="chat-sessions">
                    <Button variant="outline" size="sm" onClick={newChat} data-testid="chat-new-btn" className="w-full border-border bg-card text-foreground"><Plus className="mr-1.5 h-4 w-4" /> New conversation</Button>
                    <ul className="mt-3 space-y-1">
                        {sessions.map((s) => (
                            <li key={s.id} className={`group flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs ${s.id === sid ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-secondary"}`}>
                                <button onClick={() => open(s.id)} className="flex-1 truncate text-left" data-testid={`chat-session-${s.id}`}>{s.title}</button>
                                <button onClick={() => remove(s.id)} className="opacity-0 group-hover:opacity-100" data-testid={`chat-session-delete-${s.id}`}><Trash2 className="h-3 w-3" /></button>
                            </li>
                        ))}
                    </ul>
                </aside>
                <section className="flex h-[calc(100vh-15rem)] min-h-[28rem] flex-col rounded-2xl border border-border bg-card lg:col-span-3" data-testid="chat-panel">
                    <div className="flex-1 space-y-4 overflow-y-auto p-5" data-testid="chat-messages">
                        {messages.length === 0 && (
                            <div className="flex h-full flex-col items-center justify-center text-center">
                                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15"><Sparkles className="h-6 w-6 text-primary" /></div>
                                <p className="mt-4 font-heading text-lg font-bold text-foreground">Ask anything about your money</p>
                                <p className="mt-1 max-w-sm text-sm text-muted-foreground">Sift only uses your uploaded transactions. Try one of these:</p>
                                <div className="mt-4 flex max-w-lg flex-wrap justify-center gap-2">
                                    {SUGGESTIONS.map((s, i) => <button key={s} onClick={() => send(s)} data-testid={`chat-suggestion-${i}`} className="rounded-full border border-border bg-secondary px-3 py-1.5 text-xs text-foreground/85 hover:border-primary hover:text-foreground">{s}</button>)}
                                </div>
                            </div>
                        )}
                        {messages.map((m, i) => (
                            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}-${i}`}>
                                <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-md" : "bg-secondary text-foreground rounded-bl-md prose-sift"}`}>
                                    {m.role === "assistant" ? (m.content ? renderMd(m.content) : <Loader2 className="h-4 w-4 animate-spin text-primary" />) : m.content}
                                </div>
                            </div>
                        ))}
                        <div ref={endRef} />
                    </div>
                    <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 border-t border-border p-3">
                        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="e.g. How much did I spend at Amazon in June?" data-testid="chat-input"
                            className="flex-1 rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
                        <Button type="submit" disabled={busy || !input.trim()} data-testid="chat-send-btn" className="bg-primary text-primary-foreground hover:bg-primary/90">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                    </form>
                </section>
            </div>
        </AppShell>
    );
}
